import { useState } from "react";
import { XMarkIcon } from "@heroicons/react/24/outline";
import CsvUploadLink from "@/pages/PortfolioBuilder/components/CsvUploadLink";
import {
  parseCsvKeywords,
  parsePastedKeywords,
} from "@/pages/PortfolioBuilder/parsePastedList";
import { downloadPortfolioTemplate } from "../portfolioCsv";
import { HINT, INPUT, SECTION_LABEL } from "../ui/theme";

interface PortfolioQuickAddProps {
  tickers: string[];
  onChange: (next: string[]) => void;
}

/**
 * The names the user actually holds, asked for at the very start.
 *
 * Second on the page rather than last: it is the most specific thing anyone
 * can tell us, and the only answer here that produces a watchlist of things
 * they own rather than a category they trade. Under the markets grid it read
 * as a footnote to a set of checkboxes.
 *
 * Typed and pasted entry share one parser with the watchlist builder
 * (`parsePastedTickers`), so "AAPL, MSFT", a newline-separated paste and a CSV
 * column all behave identically here and there.
 */
export default function PortfolioQuickAdd({
  tickers,
  onChange,
}: PortfolioQuickAddProps) {
  const [value, setValue] = useState("");

  /**
   * Case is preserved deliberately.
   *
   * These go through the same tag resolution the watchlist builder uses, at
   * creation time — so "Apple" must survive as "Apple" to resolve to AAPL.
   * Upper-casing on entry produced a ticker literally called "APPLE", which
   * matches nothing in any feed.
   */
  const add = (parsed: string[]) => {
    if (parsed.length === 0) return;
    const seen = new Set(tickers.map((t) => t.toLowerCase()));
    const fresh = parsed.filter((t) => !seen.has(t.toLowerCase()));
    if (fresh.length === 0) return;
    onChange([...tickers, ...fresh]);
  };

  const commit = () => {
    add(parsePastedKeywords(value));
    setValue("");
  };

  const handleCsv = (file: File) => {
    const reader = new FileReader();
    reader.onload = () => add(parseCsvKeywords(String(reader.result ?? "")));
    reader.readAsText(file);
  };

  return (
    <div>
      {/* No icon. Two of the three sections carried one and the third did
          not, which read as decoration rather than meaning. */}
      <span className={SECTION_LABEL}>Your own portfolio</span>
      <p className={`${HINT} mt-0.5`}>
        Tickers or company names — we&apos;ll work out the rest. These become a
        watchlist, highlighted everywhere.
      </p>

      <input
        type="text"
        value={value}
        onChange={(e) => setValue(e.target.value)}
        onKeyDown={(e) => {
          if (e.key === "Enter" || e.key === ",") {
            e.preventDefault();
            commit();
          }
        }}
        // Committing on blur too: people type the last ticker and reach
        // straight for Continue, and losing it there would be silent.
        onBlur={commit}
        placeholder="e.g. AAPL, Apple, OPEC"
        className={`${INPUT} mt-2`}
      />

      <div className="mt-1.5 flex flex-wrap items-center gap-x-2 gap-y-1 text-xs">
        <CsvUploadLink onFile={handleCsv} label="Upload CSV" />
        <span className="text-gray-600" aria-hidden>
          ·
        </span>
        {/* Same weight as the upload: these are two halves of one affordance,
            and greying this one out made it look disabled. */}
        <button
          type="button"
          onClick={downloadPortfolioTemplate}
          className="cursor-pointer border-none bg-transparent p-0 text-xs text-squawk-blue transition-colors hover:text-squawk-blue/80"
        >
          Download template
        </button>
      </div>

      {tickers.length > 0 && (
        <div className="mt-2 flex flex-wrap gap-1">
          {tickers.map((ticker) => (
            <span
              key={ticker}
              className="inline-flex items-center gap-1 rounded border border-squawk-blue/30 bg-squawk-blue/10 py-0.5 pl-2 pr-1 font-mono text-xs text-squawk-blue"
            >
              {ticker}
              <button
                type="button"
                onClick={() => onChange(tickers.filter((t) => t !== ticker))}
                aria-label={`Remove ${ticker}`}
                className="cursor-pointer border-none bg-transparent p-0.5 transition-colors hover:text-white"
              >
                <XMarkIcon className="size-3" />
              </button>
            </span>
          ))}
        </div>
      )}
    </div>
  );
}
